import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  adminEmailsApiSelector,
  adminEmailsPageSelector,
  adminSuppressionsApiSelector,
  adminSuppressionsPageSelector,
} from '../store/selectors/admin.selectors';
import {fetchAdminEmails, fetchAdminSuppressions} from '../store/slices/adminThunks';
import {
  ADMIN_EMAILS_PAGE_SIZE,
  AdminEmailStatusFilter,
  EmailType,
} from '../domain/admin';
import {AdminEmailLogTable, AdminSuppressionTable} from './admin/AdminEmailLogTable';
import {Pagination, Panel} from './admin/AdminPrimitives';
import {SearchIcon, XIcon} from './Icons';

/**
 * Email: the global log, and the suppression list behind it.
 *
 * They are one screen in two tabs because they answer halves of the same question. The log says
 * *this send* did not arrive; the suppression list says *nothing will* until somebody clears it in
 * Resend. An operator looking at a bounced login link needs both, and splitting them across the nav
 * would mean noticing the second one only if you already knew it existed.
 *
 * The log defaults to `undelivered` — the server's shorthand for
 * `send_failed,bounced,complained,suppressed`. An unfiltered log is almost entirely successful
 * sends, which nobody comes here to read.
 */

type EmailTab = 'log' | 'suppressions';

const STATUS_FILTERS: Array<{key: AdminEmailStatusFilter | 'all'; label: string; hint: string}> = [
  {key: 'undelivered', label: 'Undelivered', hint: 'Failed, bounced, spam-marked or suppressed.'},
  {key: 'suppressed', label: 'Suppressed', hint: 'Accepted by Resend and silently dropped.'},
  {key: 'bounced', label: 'Bounced', hint: 'Permanently rejected by the recipient’s server.'},
  {key: 'sent', label: 'Accepted', hint: 'Handed to Resend. Not proof of delivery.'},
  {key: 'delivered', label: 'Delivered', hint: 'Confirmed by the provider.'},
  {key: 'all', label: 'All', hint: 'Every send.'},
];

const TYPE_FILTERS: Array<{key: EmailType | ''; label: string}> = [
  {key: '', label: 'Any type'},
  {key: 'login_link', label: 'Login link'},
  {key: 'welcome', label: 'Welcome'},
  {key: 'password_changed', label: 'Password changed'},
];

const SEARCH_DEBOUNCE_MS = 300;

const selectClass =
  'rounded-lg border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-200 '
  + 'focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

const AdminEmailsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const emailsPage = useAppSelector(adminEmailsPageSelector);
  const emailsApi = useAppSelector(adminEmailsApiSelector);
  const suppressionsPage = useAppSelector(adminSuppressionsPageSelector);
  const suppressionsApi = useAppSelector(adminSuppressionsApiSelector);

  const tab: EmailTab = searchParams.get('tab') === 'suppressions' ? 'suppressions' : 'log';
  const statusParam = searchParams.get('status');
  const status: AdminEmailStatusFilter | 'all' = statusParam === 'all'
    ? 'all'
    : (STATUS_FILTERS.find(entry => entry.key === statusParam)?.key ?? 'undelivered');
  const emailType = (searchParams.get('type') as EmailType | null) ?? '';
  const search = searchParams.get('search') ?? '';
  const includeRemoved = searchParams.get('include_removed') === 'true';
  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const setParams = useCallback(
    (patch: Record<string, string | undefined>, options?: {keepOffset?: boolean}) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          Object.entries(patch).forEach(([key, value]) => {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });

          if (!options?.keepOffset) {
            next.delete('offset');
          }

          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (searchInput === search) {
      return;
    }

    const timer = window.setTimeout(() => {
      setParams({search: searchInput || undefined});
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, search, setParams]);

  const emailQuery = useMemo(
    () => ({
      status: status === 'all' ? undefined : status,
      emailType: emailType || undefined,
      search: search || undefined,
      limit: ADMIN_EMAILS_PAGE_SIZE,
      offset,
    }),
    [status, emailType, search, offset]
  );

  const suppressionQuery = useMemo(
    () => ({
      includeRemoved: includeRemoved || undefined,
      search: search || undefined,
      limit: ADMIN_EMAILS_PAGE_SIZE,
      offset,
    }),
    [includeRemoved, search, offset]
  );

  // Only the visible tab fetches. The two lists page independently, and loading the one nobody is
  // looking at would double every request on a screen that is already a scan of failures.
  useEffect(() => {
    if (tab === 'log') {
      dispatch(fetchAdminEmails(emailQuery));
    } else {
      dispatch(fetchAdminSuppressions(suppressionQuery));
    }
  }, [dispatch, tab, emailQuery, suppressionQuery]);

  const isLog = tab === 'log';
  const page = isLog ? emailsPage : suppressionsPage;
  const api = isLog ? emailsApi : suppressionsApi;
  const hasFilters = Boolean(search || emailType || status !== 'all');

  return (
    <Panel
      padded={false}
      title={
        <div className="flex items-center gap-1">
          <TabButton
            label="Email log"
            isActive={isLog}
            onClick={() => setParams({tab: undefined})}
          />
          <TabButton
            label="Suppressed addresses"
            isActive={!isLog}
            onClick={() => setParams({tab: 'suppressions'})}
          />
        </div>
      }
      subtitle={
        isLog
          ? 'Every send, across all users. “Accepted” is not delivery.'
          : 'Addresses Resend refuses. Read-only — clearing one is done in the Resend dashboard.'
      }
    >
      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-800 px-4 py-3">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" />
          <input
            type="search"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Search recipient…"
            aria-label="Search by recipient address"
            className="w-56 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
             text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
             focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {isLog ? (
          <>
            {STATUS_FILTERS.map(entry => (
              <button
                key={entry.key}
                type="button"
                title={entry.hint}
                onClick={() => setParams({status: entry.key})}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
                  status === entry.key
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
                }`}
              >
                {entry.label}
              </button>
            ))}

            <select
              value={emailType}
              onChange={event => setParams({type: event.target.value})}
              aria-label="Email type"
              className={selectClass}
            >
              {TYPE_FILTERS.map(entry => (
                <option key={entry.key || 'any'} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border
           border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-300">
            <input
              type="checkbox"
              checked={includeRemoved}
              onChange={event => setParams({include_removed: event.target.checked ? 'true' : undefined})}
              className="h-3 w-3 accent-indigo-500"
            />
            Include cleared
          </label>
        )}

        <span className="ml-auto text-[11px] tabular-nums text-gray-600">
          {api.status === 'pending' ? 'Loading…' : `${(page?.total ?? 0).toLocaleString()} matching`}
        </span>
      </div>

      {isLog ? (
        <AdminEmailLogTable
          emails={emailsPage?.items ?? []}
          isLoading={emailsApi.status === 'pending'}
          error={emailsApi.status === 'rejected' ? emailsApi.error : null}
          onRetry={() => dispatch(fetchAdminEmails(emailQuery))}
          hasFilters={hasFilters}
        />
      ) : (
        <AdminSuppressionTable
          suppressions={suppressionsPage?.items ?? []}
          isLoading={suppressionsApi.status === 'pending'}
          error={suppressionsApi.status === 'rejected' ? suppressionsApi.error : null}
          onRetry={() => dispatch(fetchAdminSuppressions(suppressionQuery))}
          includeRemoved={includeRemoved}
        />
      )}

      <Pagination
        total={page?.total ?? 0}
        limit={page?.limit || ADMIN_EMAILS_PAGE_SIZE}
        offset={offset}
        onOffsetChange={next => setParams({offset: String(next)}, {keepOffset: true})}
        noun={isLog ? 'emails' : 'addresses'}
        isLoading={api.status === 'pending'}
      />
    </Panel>
  );
};

const TabButton: React.FC<{label: string; isActive: boolean; onClick: () => void}> = ({
  label,
  isActive,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
      isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
    }`}
  >
    {label}
  </button>
);

export default AdminEmailsContainer;
